import AddIcon from '@mui/icons-material/Add';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import Check from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import GridOnIcon from '@mui/icons-material/GridOn';
import RestoreIcon from '@mui/icons-material/Restore';
import {
  Button,
  ButtonGroup,
  ClickAwayListener,
  Divider,
  Grow,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  TextField,
} from '@mui/material';
import { darken, lighten, Theme } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import CryptoJS from 'crypto-js';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { CountdownCircleTimer } from 'react-countdown-circle-timer';
import { CSVLink } from 'react-csv';
import { Data } from 'react-csv/components/CommonPropTypes';
import ScaleLoader from 'react-spinners/ScaleLoader';
import { useRecoilState } from 'recoil';
import { useDebouncedCallback } from 'use-debounce/lib';

import { Logger } from '../../../../../common/Logger';
import { endingDate } from '../../../../../recoil/atoms/endingDate';
import { startingDate } from '../../../../../recoil/atoms/startingDate';

const passpharse = 'kixie.com 406 Wilshore Blvd, Santa Monica, CA 90401';

const useToolbarStyles = makeStyles((theme: Theme) => ({
  toolbar: {
    borderBottom:
      theme.palette.mode === 'dark'
        ? '1px solid rgba(81, 81, 81, 1)'
        : '1px solid rgba(224, 224, 224, 1)',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  highlight:
    theme.palette.mode === 'dark'
      ? {
          color: theme?.palette?.text?.secondary,
          backgroundColor:
            darken(theme?.palette?.primary?.dark, 0.5) + ' !important',
        }
      : {
          color: theme?.palette?.secondary?.main,
          backgroundColor: lighten(theme?.palette?.primary?.light, 0.85),
        },
  button: {
    marginRight: theme.spacing(1),
  },
  disabled:
    theme.palette.mode === 'dark'
      ? {
          backgroundColor: 'grey',
          '&:hover': {
            backgroundColor: 'darkgrey',
          },
        }
      : {
          backgroundColor: 'lightgrey',
          '&:hover': {
            backgroundColor: 'grey',
          },
        },
  deleteButton: {
    backgroundColor: theme?.custom?.button?.delete?.main,
    color: theme?.custom?.button?.delete?.contrastText,
    '&:hover': {
      backgroundColor: theme?.custom?.button?.delete?.dark,
    },
    whiteSpace: 'nowrap',
  },
  search: {
    marginTop: '5px', // zero out material-ui dense
    marginBottom: 0, // zero out material-ui dense
    marginRight: '1vw', // space for loading icon
  },
  toolbarItem: {
    marginRight: '5px',
    marginTop: '5px',
    marginBottom: 0,
  },
  toolbarButton: {
    paddingTop: '7px',
    paddingBottom: '7px',
  },
  leftComponent: {
    display: 'flex',
    flex: '1',
    alignItems: 'center',
    alignContent: 'flex-start',
    justifyContent: 'flex-start',
    // backgroundColor: '#EBEBEB',
  },
  centerComponent: {
    display: 'flex',
    flex: '1',
    alignItems: 'baseline',
    alignContent: 'center',
    justifyContent: 'center',
    // backgroundColor: '#ABBAEA',
  },
  centerComponentMaxWidth: {
    maxWidth: '200px',
  },
  rightComponent: {
    display: 'flex',
    alignItems: 'flex-end',
    alignContent: 'flex-end',
    justifyContent: 'flex-end',
    // backgroundColor: '#FBD603',
  },
  timer: {
    //fontFamily: 'Montserrat',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  timerValue: {
    color: 'white',
    fontSize: '0.75rem',
  },
  timerWrapper: {
    display: 'flex',
    justifyContent: 'center',
    paddingRight: '4px',
  },
  dropDownButton: {
    paddingLeft: '0',
    paddingRight: '0',
  },
  dropDownMenu: {
    zIndex: 30,
    // backgroundColor: theme.palette.primary.main,
    backgroundColor:
      theme.palette.mode === 'dark' ? '#121c21 !important' : '#fff',
  },
  restoreIcon: {
    paddingRight: '4px',
  },
  readonly: {
    backgroundColor: theme.palette.mode === 'dark' ? '#121c21' : '#f6f6f6',
  },
}));

export enum refreshDurations {
  // eslint-disable-next-line no-unused-vars
  // dur15sec = 15,
  // eslint-disable-next-line no-unused-vars
  dur30sec = 30,
  // eslint-disable-next-line no-unused-vars
  dur1min = 60,
  // eslint-disable-next-line no-unused-vars
  dur3min = 180,
  // eslint-disable-next-line no-unused-vars
  dur5min = 300,
}
interface RefreshOptionsItem {
  name: string;
  dur: number;
}

const refreshOptions: RefreshOptionsItem[] = [
  { name: 'Auto Refresh', dur: 0 },
  // { name: '15 Seconds', dur: refreshDurations.dur15sec },
  { name: '30 Seconds', dur: refreshDurations.dur30sec },
  { name: '1 Minute', dur: refreshDurations.dur1min },
  { name: '3 Minutes', dur: refreshDurations.dur3min },
  { name: '5 Minutes', dur: refreshDurations.dur5min },
];

const renderTime = ({ remainingTime }) => {
  const classes = useToolbarStyles();
  let timeToDisplay = '';

  if (remainingTime < 61) {
    timeToDisplay = remainingTime;
  } else {
    const minutes = Math.floor(remainingTime / 60);
    const seconds = remainingTime % 60;
    timeToDisplay = `${minutes}:${seconds < 10 ? '0' + seconds : seconds}`;
  }

  return (
    <div className={classes.timer}>
      <div className={classes.timerValue}>{timeToDisplay}</div>
    </div>
  );
};

const getAutoRefreshLocalSettings = (
  id: string,
  defaultAutoRefresh: boolean,
  defaultDuration: number
): { autoRefresh: boolean; duration: number } => {
  const localStorageVal = localStorage.getItem('autoRefreshSettings-' + id);
  if (localStorageVal) {
    try {
      const retval = JSON.parse(
        CryptoJS.AES.decrypt(localStorageVal, passpharse).toString(
          CryptoJS.enc.Utf8
        )
      );
      return retval;
    } catch (error) {
      Logger.error(
        {
          message:
            'EnhancedTableToolbar.tsx: Unable to parse AutoRefresh Settings',
          error,
        },
        'EnhancedTableToolbar'
      );
    }
  }
  return {
    autoRefresh: defaultAutoRefresh,
    duration: defaultDuration,
  };
};
const setAutoRefreshLocalSettings = (
  id: string,
  newAutoRefresh: boolean,
  newDuration: number
) => {
  localStorage.setItem(
    'autoRefreshSettings-' + id,
    CryptoJS.AES.encrypt(
      '{ "autoRefresh":' +
        (newAutoRefresh ? 'true' : 'false') +
        ', "duration":' +
        newDuration +
        '}',
      passpharse
    ).toString()
  );
};

interface IRefreshButton {
  /** Unique ID (across dashboard) or this button to store settings in Storage */
  id: string;
  /** The duration before onClickRefresh funtion is called */
  duration: refreshDurations;
  /** The function to call when refresh is invoked */
  onClickForRefresh: () => void;
  isLoading?: boolean;
  disabled?: boolean;
  /** If true the auto refresh functionality is enabled otherwise the user has to press the refresh button */
  autoRefresh?: boolean;
  /** Set to false if you do not want the user to be able to change refresh settings */
  showRefreshDropDownOptions?: boolean;
  /** the text to show in the button - default "refresh" */
  refreshButtonText?: string;
}

/**
 * Create an Auto Refresh button.  The button can have a drop down will alternate list of refresh
 *
 * @param id Unique ID (across dashboard) for this button to store settings in Local Storage
 * @param duration The duration before onClickRefresh funtion is called
 * @param onClickForRefresh The function to call when refresh is invoked
 * @param autoRefresh If true the auto refresh functionality is enabled otherwise the user has to press the refresh button
 * @param showRefreshDropDownOptions Set to false if you do not want the user to be able to change refresh settings
 * @param refreshButtonText the text to show in the button - default "refresh"
 */
export const ToolbarRefreshButton = ({
  id,
  duration = refreshDurations.dur30sec,
  onClickForRefresh,
  isLoading = false,
  disabled = false,
  autoRefresh = true,
  showRefreshDropDownOptions = true,
  refreshButtonText = 'Refresh',
}: IRefreshButton) => {
  const lsSettings = getAutoRefreshLocalSettings(id, autoRefresh, duration);
  // eslint-disable-next-line no-console
  console.log('lsSettings=', lsSettings);
  // overwrite the passed in values from local storage if exists
  ({ autoRefresh, duration } = getAutoRefreshLocalSettings(
    id,
    autoRefresh,
    duration
  ));

  // eslint-disable-next-line no-console
  console.log('autoRefresh=', autoRefresh);
  const classes = useToolbarStyles();

  // dropdown states
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef(null);

  // timer states
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshAuto, setRefreshAuto] = useState(autoRefresh);
  const [timerDuration, setTimerDuration] = useState(duration);
  const [remainingTime, setRemainingTime] = useState(duration | 0);

  const handleMenuItemClick = (
    refreshName: string,
    refreshValue: number,
    index: number
  ) => {
    if (refreshValue === 0) {
      setAutoRefreshLocalSettings(id, !refreshAuto, timerDuration);
      setRefreshAuto(!refreshAuto);
      setRemainingTime(0);
    } else {
      setAutoRefreshLocalSettings(id, refreshAuto, refreshValue);
      setRemainingTime(refreshValue);
      setTimerDuration(refreshValue);
      // setSelectedIndex(index);
    }
    setOpen(false);
    setRefreshKey((prevKey) => prevKey + 1);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (
      anchorRef.current &&
      (anchorRef.current as any).contains(event.target)
    ) {
      return;
    }

    setOpen(false);
  };

  return (
    <React.Fragment>
      <Tooltip
        title={refreshButtonText.length > 0 ? refreshButtonText : 'REFRESH'}
      >
        <span>
          <ButtonGroup variant="contained" ref={anchorRef}>
            <Button
              className={clsx({
                [classes.disabled]: isLoading,
              })}
              name={refreshButtonText}
              variant="contained"
              color="primary"
              onClick={() => {
                setRefreshKey((prevKey) => prevKey + 1);
                onClickForRefresh();
              }}
              disableElevation
              disabled={disabled}
            >
              <span className={classes.timerWrapper}>
                {refreshAuto ? (
                  <CountdownCircleTimer
                    key={refreshKey}
                    isPlaying
                    size={26}
                    strokeWidth={2}
                    initialRemainingTime={remainingTime}
                    duration={timerDuration}
                    trailColor={isLoading || disabled ? 'lightgray' : '#0098C7'}
                    colors={[
                      ['#df9b37', 1],
                      // ['#17179B', 0.33],
                      // ['#536FA7', 0.33],
                      // ['#57AB66', 0.33],
                    ]}
                    onComplete={() => {
                      onClickForRefresh();
                      return [refreshAuto, 100];
                    }}
                  >
                    {renderTime}
                  </CountdownCircleTimer>
                ) : (
                  <span className={classes.restoreIcon}>
                    <RestoreIcon />
                  </span>
                )}
              </span>
              {refreshButtonText}
            </Button>
            {showRefreshDropDownOptions && (
              <Button
                className={clsx(classes.dropDownButton, {
                  [classes.disabled]: isLoading,
                })}
                size="small"
                onClick={handleToggle}
                variant="contained"
                color="primary"
                disableElevation
                disabled={disabled}
              >
                <ArrowDropDown fontSize="large" />
              </Button>
            )}
          </ButtonGroup>
        </span>
      </Tooltip>
      <Popper
        className={classes.dropDownMenu}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper className={classes.dropDownMenu}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList dense id="split-button-menu">
                  {refreshOptions.map((option, index) => (
                    <MenuItem
                      key={option.name}
                      //selected={index === selectedIndex}
                      // onClick={(event) => handleMenuItemClick(event, index)}
                      onClick={() =>
                        handleMenuItemClick(option.name, option.dur, index)
                      }
                      divider={option.dur === 0}
                    >
                      {((option.dur === 0 && refreshAuto) ||
                        option.dur == timerDuration) && (
                        <ListItemIcon>
                          <Check />
                        </ListItemIcon>
                      )}
                      <ListItemText
                        inset={
                          (option.dur === 0 && !refreshAuto) ||
                          (option.dur != 0 && option.dur != timerDuration)
                        }
                      >
                        {option.name}
                      </ListItemText>
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </React.Fragment>
  );
};

interface IAddDeleteButton {
  numSelected?: number;
  onClickForAdd: () => void;
  onClickForDelete?: () => void;
  isLoading?: boolean;
  disabled?: boolean;
  addButtonText?: string;
  dataPup?: string;
  className?: string;
}

export const ToolbarAddDeleteButton = ({
  numSelected = 0,
  onClickForAdd,
  onClickForDelete,
  isLoading = false,
  disabled = false,
  addButtonText = 'Add',
  dataPup = undefined,
  className = '',
}: IAddDeleteButton) => {
  const classes = useToolbarStyles();

  return numSelected > 0 ? (
    <Tooltip title="Delete">
      <span>
        <Button
          aria-label="delete"
          variant="contained"
          className={`${clsx(
            classes.deleteButton,
            classes.button
          )} ${className}`}
          startIcon={<DeleteIcon />}
          onClick={onClickForDelete}
          disableElevation
          disabled={disabled}
        >
          Delete ({numSelected})
        </Button>
      </span>
    </Tooltip>
  ) : (
    <Tooltip title={addButtonText}>
      <span>
        <Button
          data-pup={dataPup ?? undefined}
          className={`${clsx(classes.button, {
            [classes.disabled]: isLoading,
          })} ${className}`}
          name={addButtonText}
          variant="contained"
          startIcon={<AddIcon />}
          color="primary"
          onClick={onClickForAdd}
          disableElevation
          disabled={disabled}
        >
          {addButtonText}
        </Button>
      </span>
    </Tooltip>
  );
};

interface IEnhancedTableToolbar {
  /** Styling for toolbar */
  className?: string;
  /** Show highlight if rows selected */
  numSelected?: number;
  /** Search text */
  search: string;
  /** Callback used to set search text */
  setSearch: (value: string) => void;
  /** csvData from csv react package */
  csvData?: Data;
  /** Show skeleton on loading */
  isLoading?: boolean;
  /** Debounced timeout to set search field */
  debounceTimeout?: number;
  /** Component added to the left side of the toolbar */
  leftComponent?: React.ReactNode;
  /** Component added to the center of the toolbar */
  centerComponent?: React.ReactNode;
  /** Component added to the right side of the toolbar */
  rightComponent?: React.ReactNode;
  /** When true changes changes add button name to cadence */
  cadence?: boolean;
  /**Type of Data being passed in adds to fileName export */
  dataType?: string;
  /** When true display scale loader while searching */
  scaleLoadingForSearch?: boolean;
  /** When true display search input */
  hideSearch?: boolean;
  /** Make the center component have a max width */
  smallerCenterComponent?: boolean;
  /** Hide vertical divider next to left component */
  hideVerticalDivider?: boolean;
  csvExport?: boolean;
  onCsvExport?: any;
  customSearchField?: React.ReactNode | undefined;
  hideCsvExportButton?: boolean;
  hideScaleLoader?: boolean;
}

export const EnhancedTableToolbar = ({
  className,
  numSelected = 0,
  search,
  setSearch,
  csvData,
  isLoading,
  debounceTimeout = 100,
  leftComponent,
  centerComponent,
  rightComponent,
  dataType = 'data',
  scaleLoadingForSearch = false,
  hideSearch = false,
  smallerCenterComponent = false,
  hideVerticalDivider = false,
  csvExport = false,
  onCsvExport,
  customSearchField,
  hideCsvExportButton = false,
  hideScaleLoader = false,
}: IEnhancedTableToolbar) => {
  const classes = useToolbarStyles();
  const [searchLoading, setSearchLoading] = useState(false);
  const [start] = useRecoilState(startingDate);
  const [end] = useRecoilState(endingDate);
  const { callback: setSearchDebounce } = useDebouncedCallback(
    (value: string) => {
      setSearch(value);
      scaleLoadingForSearch && setSearchLoading(false);
    },
    debounceTimeout
  );

  /**
   * Create handler since synthetic event will not last on next call (setSearchDebounce)
   * @param event Change event
   */
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    scaleLoadingForSearch && setSearchLoading(true);
    setSearchDebounce(event.target.value);
  };

  return (
    <Toolbar
      className={clsx(className, classes.toolbar, {
        [classes.highlight]: numSelected > 0,
      })}
    >
      <div className={classes.leftComponent}>
        {csvData && !csvExport && !hideCsvExportButton && (
          <CSVLink
            filename={`Kixie-${dataType}-${moment(start).format(
              'YYYY-MM-DD'
            )} - ${moment(end).format('YYYY-MM-DD')}`}
            data={csvData ?? ''}
            className={classes.toolbarItem}
          >
            <Button
              className={classes.toolbarButton}
              variant="outlined"
              color="primary"
              startIcon={<GridOnIcon />}
            >
              CSV
            </Button>
          </CSVLink>
        )}
        {csvExport && !hideCsvExportButton && (
          <div className={classes.toolbarItem}>
            <Button
              className={classes.toolbarButton}
              variant="outlined"
              color="primary"
              onClick={() => onCsvExport(dataType, start, end, csvData)}
              startIcon={<GridOnIcon />}
            >
              CSV
            </Button>
          </div>
        )}

        {!hideSearch &&
          (customSearchField ?? (
            <TextField
              variant="outlined"
              className={classes.search}
              id="searchBox"
              inputProps={{
                'data-pup': 'search-bar-input',
              }}
              size="small"
              color="primary"
              defaultValue={search}
              label="Search"
              type="search" // show x (ie. clear)
              onChange={handleSearchChange}
              data-pup="table-search"
            />
          ))}
        {hideScaleLoader ? null : (
          <ScaleLoader
            loading={scaleLoadingForSearch ? searchLoading : isLoading}
            height={15}
            width={1}
          />
        )}
        {leftComponent && (
          <>
            {!hideVerticalDivider && (
              <Divider orientation="vertical" flexItem />
            )}
            {leftComponent}
          </>
        )}
      </div>
      {centerComponent && (
        <div
          className={`${classes.centerComponent} ${
            smallerCenterComponent && classes.centerComponentMaxWidth
          }`}
        >
          {centerComponent}
        </div>
      )}
      <div className={classes.rightComponent}>{rightComponent}</div>
    </Toolbar>
  );
};

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number,
  search: PropTypes.string.isRequired,
  setSearch: PropTypes.func.isRequired,
  toolbarParams: PropTypes.any,
  isLoading: PropTypes.bool,
  debounceTimeout: PropTypes.number,
};
